import type { AvatarModel, AvatarPolarity } from "@domo/shared"

// Sprint 5 — Mapping de mallas 3D por firma del Avatar.
//
// Cada combinación (Material × Polaridad) puede tener un .glb publicado
// en R2 con la malla orgánica que corresponde. Si la entrada está en
// `null`, el Avatar fallbackea al icosaedro emisivo de Sprint 1/3.
//
// Los archivos `.glb` deben estar hosteados con CORS abierto (R2
// permite eso por bucket). Tres.js los carga como ArrayBuffer y los
// procesa client-side.
//
// ── CÓMO ACTUALIZAR UN AVATAR EXISTENTE (Cristal Alfa / Omega, etc.):
// 1. Subir el .glb nuevo a R2 sobreescribiendo el archivo en el path
//    actual (ej. "Domo Cero/Avatares/1M.glb"). El path queda igual.
// 2. Bumpar el sufijo `?v=YYYY-MM-DD-N` en TODAS las URLs de este
//    archivo (un único find-replace). El cambio del query param
//    invalida el cache del browser, del CDN de R2 y del cache interno
//    de useGLTF. Sin este bump, los Tripulantes verán el GLB viejo
//    hasta que vacíen su cache.
// 3. Redeploy del cliente para que la nueva URL entre a producción.
//
// ── CÓMO AGREGAR UNA FIRMA NUEVA (Plasma Alfa, Biopolímero Omega…):
// 1. Subir el .glb al bucket bajo el prefijo "Domo Cero/Avatares/".
// 2. Reemplazar el `null` correspondiente abajo con
//    `{ url, scale, offsetY }`. `scale` ajusta el tamaño (1 = altura
//    nominal del icosaedro). `offsetY` mueve la malla en vertical
//    para que la base toque el suelo del Domo.
// 3. Redeploy del cliente. La carga es lazy con useGLTF.preload
//    cuando algún Tripulante se materializa con esa firma.

export interface AvatarMeshDef {
    /** URL pública del .glb (R2 con CORS abierto). */
    url: string
    /** Multiplicador de escala uniforme. 1 = tamaño base. */
    scale: number
    /** Desplazamiento en Y para que la base de la malla toque el piso. */
    offsetY: number
    /** Rotación inicial (Y) en radianes. 0 = mira hacia +Z. */
    rotationY?: number
}

const MESHES: Record<AvatarModel, Record<AvatarPolarity, AvatarMeshDef | null>> = {
    cristal: {
        // 2026-05-05 — Modelos rigged Cristal entregados por Zak (Meshy):
        //   · Alfa  (masculino)  → 1M.glb (rename de "AM 1.glb")
        //   · Omega (femenino)   → 1F.glb
        // Ambos optimizados a ~15k caras (antes 161k crasheaba el
        // WebGL context tras 12s). Animaciones incluidas: Inactivo 11
        // (idle), Caminando, Corriendo, Gran Ola Hola (Alfa) /
        // Agita una Mano (Omega) — el resolver matchea por substring
        // case-insensitive y respeta los nombres en español.
        // Sumamos `?v=` para invalidar el cache de browser/R2/`useGLTF`
        // cada vez que Zak reemplace el asset.
        alfa: {
            url: "https://pub-94bd1d71bb304c91ad7b8e146063f337.r2.dev/Domo%20Cero/Avatares/1M.glb",
            scale: 1,
            offsetY: 0,
        },
        omega: {
            url: "https://pub-94bd1d71bb304c91ad7b8e146063f337.r2.dev/Domo%20Cero/Avatares/1F.glb",
            scale: 1,
            offsetY: 0,
        },
    },
    plasma: {
            alfa: {
            url: "https://pub-94bd1d71bb304c91ad7b8e146063f337.r2.dev/Domo%20Cero/Avatares/2Hombre.glb",
            scale: 1,
            offsetY: 0,
        },
        omega: {
            url: "https://pub-94bd1d71bb304c91ad7b8e146063f337.r2.dev/Domo%20Cero/Avatares/2Mujer.glb",
            scale: 1,
            offsetY: 0,
        },
    },
    biopolimero: {
          alfa: {
            url: "https://pub-94bd1d71bb304c91ad7b8e146063f337.r2.dev/Domo%20Cero/Avatares/3Hombre.glb",
            scale: 1,
            offsetY: 0,
        },
        omega: {
            url: "https://pub-94bd1d71bb304c91ad7b8e146063f337.r2.dev/Domo%20Cero/Avatares/3Mujer.glb",
            scale: 1,
            offsetY: 0,
        },
    },
}

export function getAvatarMesh(
    model: AvatarModel | null,
    polarity: AvatarPolarity | null
): AvatarMeshDef | null {
    if (!model || !polarity) return null
    return MESHES[model][polarity]
}
